import React, { useEffect, useState, forwardRef, useRef, useImperativeHandle } from 'react';
import { Country, State } from 'country-state-city';
import styles from './Payment.module.css';
import {
  REACT_APP_BILLING_PORTAL_HOST,
  REACT_APP_CHARGIFY_PUBLIC_KEY,
  REACT_APP_CHARGIFY_SERVER_HOST
} from "../../../../env";
import axios from 'axios';

const Payment = forwardRef(({ formData, setGlobalError }, ref) => {
  const chargifyForm = useRef();
  const chargify = useRef();
  const [prefill, setPrefill] = useState(false);
  const [address, setAddress] = useState({});

  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!chargify.current && typeof window !== 'undefined' && window.Chargify) {
      chargify.current = new window.Chargify();
    }
  }, []);

  const countries = Country.getAllCountries().filter(c => ['US', 'CA', 'MX'].includes(c.isoCode));
  const states = State.getStatesOfCountry(address.country || 'US');

  const getStateLabel = () => {
    if (address.country === 'CA') return 'Province';
    return 'State';
  };

  const getZipLabel = () => {
    if (address.country === 'US') return 'Zip Code';
    return 'Postal Code';
  };

  if (process.env.NODE_ENV === 'development') {
    console.log('Payment initialized');
  }
  useImperativeHandle(ref, () => ({
    async tokenize() {
      if (!isReady) {
        throw new Error("Payment fields are not yet loaded. Please wait a moment.");
      }
      return new Promise((resolve, reject) => {
        chargify.current.token(
          chargifyForm.current,
          (token) => resolve(token),
          (error) => reject(error)
        );
      });
    }
  }));

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    if (name === 'country') {
        setAddress(prev => ({ ...prev, [name]: value, state: '', zip: '' }));
    } else {
        setAddress(prev => ({ ...prev, [name]: value }));
    }
  };

  useEffect(() => {
    const fieldStyles = {
      label: {
        fontSize: '1rem'
      },
      input: {
        borderRadius: '8px',
        fontSize: '1rem',
        paddingTop: '12px',
        paddingBottom: '12px',
        color: '#333',
        '::placeholder': {
          color: '#a0aec0',
        }
      },
    };

    if (process.env.NODE_ENV === 'development') {
      console.warn('Chargify Public Key:', REACT_APP_CHARGIFY_PUBLIC_KEY);
      console.warn('Chargify Server Host:', REACT_APP_CHARGIFY_SERVER_HOST);
    }

    const initChargify = async (cancelled) => {
      let sub;
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        sub = crypto.randomUUID();
      } else {
        // Fallback to a longer, more collision-resistant random string if crypto.randomUUID is unavailable
        const randomString = () => Math.random().toString(36).substring(2);
        sub = `${randomString()}-${randomString()}-${randomString()}`;
      }

      try {
        if (process.env.NODE_ENV === 'development') {
          console.log('Fetching security token for sub:', sub);
        }
        const { data } = await axios.post(`${REACT_APP_BILLING_PORTAL_HOST}/maxio/auth/security_token?sub=${sub}`, {}, {
          headers: {
            'Accept': 'application/json',
          },
        });

        if (cancelled.value) return;

        const securityToken =
          data?.security_token ||
          data?.securityToken ||
          data?.token;

        if (!securityToken) {
          throw new Error('Security token not found in response.');
        }

        chargify.current.load({
          type: 'card',
          selector: '#chargify-form',
          publicKey: REACT_APP_CHARGIFY_PUBLIC_KEY,
          serverHost: REACT_APP_CHARGIFY_SERVER_HOST,
          securityToken: securityToken,
          creditCardExpirationDateDropdowns: true,
          hideCardImage: true,
          style: fieldStyles,
          fields: {
            number: { selector: '#cc-number', placeholder: '• • • •  • • • •  • • • •  • • • •'},
            month: { selector: '#cc-month', placeholder: 'MM' },
            year: { selector: '#cc-year', placeholder: 'YYYY' },
            cvv: { selector: '#cc-cvv', placeholder: '• • •' },
            firstName: { selector: '#first', placeholder: 'John', required: true },
            lastName: { selector: '#last', placeholder: 'Doe', required: true },
            email: { selector: '#email', placeholder: 'joe@chargify.test', required: true },
          },
        });
        if (!cancelled.value) setIsReady(true);
      } catch (err) {
        if (cancelled.value) return;
        console.error('Failed to initialize Chargify with security token:', err);
        if (setGlobalError) {
          setGlobalError('Failed to load payment form. Please refresh and try again.');
        }
      }
    };

    const cancelled = { value: false };
    initChargify(cancelled);

    const chargifyInstance = chargify.current;
    return () => {
      cancelled.value = true;
      if (chargifyInstance && typeof chargifyInstance.unload === 'function') {
        chargifyInstance.unload();
      }
    };
  }, [setGlobalError]);

  useEffect(() => {
    if (prefill) {
      setAddress({
        address: formData.address,
        address2: formData.address_2,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        country: formData.country,
      });
    } else {
      setAddress({
        address: '', address2: '', city: '', state: '', zip: '', country: 'US',
      });
    }
  }, [prefill, formData]);

  return (
    <>
      <h2 className={styles.stepTitle}>Payment Information</h2>
      {!isReady && <div className={styles.loadingOverlay}>Loading secure payment fields...</div>}
      <form ref={chargifyForm} style={{ display: isReady ? 'block' : 'none' }}>
        <div id="chargify-form">
          <div className={styles.chargifyFieldsWrapper}>
            <div className={styles.chargifyRow}>
              <div id="cc-number" className={styles.chargifyField}></div>
              <div id="cc-cvv" className={styles.chargifyField}></div>
            </div>
            <div className={styles.chargifyRow}>
              <div id="cc-month" className={styles.chargifyField}></div>
              <div id="cc-year" className={styles.chargifyField}></div>
            </div>
            <div className={styles.chargifyRow}>
              <div id="first" className={styles.chargifyField}></div>
              <div id="last" className={styles.chargifyField}></div>
            </div>
            <div className={styles.chargifyRow}>
              <div id="email" className={styles.chargifyField}></div>
            </div>
          </div>
        </div>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={prefill}
              onChange={() => setPrefill(!prefill)}
              style={{ marginRight: '8px' }}
            />
            Prefill Using Address from Step 1
          </label>
        </div>
        <div className={styles.inputGroup}>
            <label htmlFor="payment_address">Address</label>
            <input type="text" id="payment_address" name="address" value={address.address || ''} onChange={handleAddressChange} disabled={prefill} />
        </div>
        <div className={styles.inputGroup}>
            <label htmlFor="payment_address2">Address Line 2 (Optional) </label>
            <input type="text" id="payment_address2" name="address2" value={address.address2 || ''} onChange={handleAddressChange} disabled={prefill} />
        </div>
        <div className={styles.inputGrid}>
            <div className={styles.inputGroup}>
                <label htmlFor="payment_country">Country</label>
                <select id="payment_country" name="country" value={address.country || ''} onChange={handleAddressChange} disabled={prefill}>
                    <option value="">Select Country</option>
                    {countries.map(c => (
                        <option key={c.isoCode} value={c.isoCode}>{c.name}</option>
                    ))}
                </select>
            </div>
            <div className={styles.inputGroup}>
                <label htmlFor="payment_city">City</label>
                <input type="text" id="payment_city" name="city" value={address.city || ''} onChange={handleAddressChange} disabled={prefill} />
            </div>
            <div className={styles.inputGroup}>
                <label htmlFor="payment_state">{getStateLabel()}</label>
                <select id="payment_state" name="state" value={address.state || ''} onChange={handleAddressChange} disabled={prefill}>
                    <option value="">Select {getStateLabel()}</option>
                    {states.map(s => (
                        <option key={s.isoCode} value={s.isoCode}>{s.name}</option>
                    ))}
                </select>
            </div>
            <div className={styles.inputGroup}>
                <label htmlFor="payment_zip">{getZipLabel()}</label>
                <input type="text" id="payment_zip" name="zip" value={address.zip || ''} onChange={handleAddressChange} disabled={prefill} placeholder={address.country === 'US' ? '12345' : 'Postal Code'} />
            </div>
        </div>

        <div>
          <input type="hidden" data-chargify="address" value={address.address || ''} />
          <input type="hidden" data-chargify="address2" value={address.address2 || ''} />
          <input type="hidden" data-chargify="city" value={address.city || ''} />
          <input type="hidden" data-chargify="state" value={address.state || ''} />
          <input type="hidden" data-chargify="zip" value={address.zip || ''} />
          <input type="hidden" data-chargify="country" value={address.country || ''} />
        </div>
        <div className={styles.chargifyDisclaimerContainer}>
          <label className={styles.chargifyDisclaimer}>
            Your 14 day free trial will begin once your MedXCom account has been activated.
            MedXCom will automatically continue your subscription at the end of your free trial and charge the subscription fee to the card entered on a monthly basis until you cancel.
            There are no refunds or credits for partial months.
          </label>
        </div>
      </form>
    </>
  );
});

export default Payment;
